import React from "react";
import ContactUs from "../../../components/ContactUs.js";
import { Helmet } from "react-helmet-async";

const services = [
  {
    logo_images: "../assets/icons/prometheus/service-1.png",
    title: "Metrics Collection",
    description:
      "Instrument applications, infrastructure, and network components with Prometheus client libraries, exporters, and service discovery for complete metric coverage.",
  },
  {
    logo_images: "../assets/icons/prometheus/service-2.png",
    title: "Exporter Management",
    description:
      "Deploy and manage node, cloud, database, and custom exporters with auto-discovery, TLS, and labeling standards to deliver reliable telemetry at scale.",
  },
  {
    logo_images: "../assets/icons/prometheus/service-3.png",
    title: "Dashboards & Visualization",
    description:
      "Design Grafana dashboards that surface SLIs, SLOs, and business KPIs with templating, annotations, drill-downs, and multi-cloud observability overlays.",
  },
  {
    logo_images: "../assets/icons/prometheus/service-4.png",
    title: "Alerting & Incident Response",
    description:
      "Configure Alertmanager routing, deduplication, and escalation policies integrated with PagerDuty, Slack, ServiceNow, and Opsgenie for rapid response.",
  },
  {
    logo_images: "../assets/icons/prometheus/service-5.png",
    title: "Monitoring Strategy & Governance",
    description:
      "Establish monitoring playbooks, tagging conventions, retention policies, and runbooks aligning engineering, SRE, and business stakeholders.",
  },
];

const benefits = [
  "Real-time visibility into application and infrastructure health",
  "Data-driven decisions with actionable SLO dashboards",
  "Reduced MTTR through proactive alerting and runbook automation",
  "Consistent monitoring standards across hybrid and multi-cloud estates",
  "Cost-efficient telemetry with optimized retention and federation",
  "Improved collaboration between Dev, Ops, and Business teams",
];

const expertise = [
  "Certified Prometheus and Grafana practitioners",
  "Experience operating large-scale time-series clusters",
  "Custom exporter development and service discovery integration",
  "Multi-tenant Grafana governance with RBAC and folder strategies",
  "Dashboards for Kubernetes, microservices, data, and IoT workloads",
  "Automation with Terraform, Helm, and GitOps for monitoring stacks",
];

const useCases = [
  {
    title: "Kubernetes Fleet Observability",
    description:
      "Implemented Prometheus Operator and federated Grafana dashboards to monitor 80+ clusters, surfacing latency SLIs and auto-generating SLO burn alerts.",
  },
  {
    title: "FinTech Reliability Program",
    description:
      "Delivered SLO-driven monitoring with synthetic checks, business KPIs, and automated escalation policies, reducing MTTR by 55%.",
  },
  {
    title: "Edge & IoT Monitoring",
    description:
      "Built lightweight Prometheus edge collectors with centralized Grafana views for 5,000+ devices, providing predictive maintenance insights.",
  },
  {
    title: "Hybrid Cloud AIOps",
    description:
      "Combined Prometheus metrics with logs and traces, feeding anomaly detection pipelines that proactively flagged capacity issues across regions.",
  },
];

const reasons = [
  "Battle-tested reference architectures for HA Prometheus and Grafana",
  "Security-first deployment with TLS, auth proxies, and single sign-on",
  "Integration expertise with Loki, Tempo, Jaeger, and third-party APMs",
  "Enablement programs covering SLO design, dashboarding, and alert hygiene",
  "Flexible engagement: assessments, implementation, or managed observability",
  "24/7 operations with SLO-driven support and continuous improvements",
];

const scrollToContact = () => {
  document
    .getElementById("contact-section")
    ?.scrollIntoView({ behavior: "smooth" });
};

const Monitoring = () => (
  <>
    <Helmet>
      <meta
        name="description"
        content="Ayanshtech Solutions — Prometheus & Grafana monitoring services covering metrics, exporters, dashboards, alerts, and observability strategy."
      />
      <meta name="copyright" content="codeglim" />
      <meta
        property="og:title"
        content="Prometheus & Grafana Monitoring | Observability Services | Ayanshtech Solutions"
      />
      <meta property="og:site_name" content="Ayanshtech Solutions" />
      <meta
        property="og:url"
        content="http://ayanshtechnology.com/devops-monitoring"
      />
      <meta
        property="og:description"
        content="Prometheus & Grafana monitoring solutions for metrics, dashboards, and alerting."
      />
      <meta property="og:type" content="website" />
      <meta
        property="og:image"
        content="http://ayanshtechnology.com/assets/logo.png"
      />
      <link
        rel="canonical"
        href="http://ayanshtechnology.com/devops-monitoring"
      />
      <title>
        Prometheus & Grafana Monitoring | Observability Services | Ayanshtech
        Solutions
      </title>
    </Helmet>

    <div className="flex flex-col items-center justify-center mb-6 p-6">
      <section className="py-5 w-full">
        <div className="flex flex-col lg:flex-row items-center lg:space-x-16">
          <div className="lg:w-3/4 lg:text-left p-4">
            <h1 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-4">
              Prometheus & Grafana Monitoring
            </h1>
            <p className="sm:text-base md:text-lg lg:text-lg mb-3">
              Build a unified observability platform with Prometheus metrics and
              Grafana dashboards tailored to your SLOs and business KPIs. We
              instrument applications, infrastructure, and networks to deliver
              proactive insights and faster incident response.
            </p>
            <button
              onClick={scrollToContact}
              className="bg-green-600 text-white hover:bg-yellow-500 hover:text-green-700 hover:border hover:border-yellow-500 font-semibold py-2 px-4 rounded-full mt-6"
            >
              Design Your Monitoring Strategy
            </button>
          </div>
          <div className="flex justify-center mb-12 lg:mb-0">
            <img
              src="../assets/images/devops-pages/Grafana-banner.svg"
              alt="Prometheus & Grafana"
              className="w-full h-auto sm:w-3/4 md:w-2/3 lg:w-full object-contain"
            />
          </div>
        </div>
      </section>

        {/* <section className="mb-5 mt-10 p-4 w-full">
          <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
            About Prometheus & Grafana
          </h2>
          <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
            Prometheus captures time-series metrics while Grafana turns that data
            into actionable visualizations. Together they form the backbone of
            cloud-native observability, enabling teams to define SLIs, monitor
            SLOs, and troubleshoot issues before they impact users.
          </p>
        </section> */}

      <section className="text-center mb-5 mt-12  w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Monitoring Services
        </h2>
        <p className="sm:text-base md:text-lg lg:text-xl text-gray-600 font-semibold mt-4 max-w-6xl mx-auto">
          End-to-end observability services covering metrics, exporters,
          dashboards, and alerting governance.
        </p>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
             
            >
               <div className="flex items-center gap-2">
                <div className="w-[60px] h-[60px]  ">
                  <img src={service.logo_images} className="w-full" alt="" />
                </div>
                <h3 className="text-lg font-bold">{service.title}</h3>
              </div>
              <p className="text-lg text-gray-700">{service.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-20 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Benefits of Prometheus & Grafana
        </h2>
      </section>
      <div className="p-4 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-blue-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{benefit}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="p-6 sm:px-8 md:px-12 lg:px-16 w-full  mt-10 ">
        <div className="text-center mb-8">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Our Monitoring Expertise
          </h2>
        </div>
        <div className="flex justify-center flex-col md:flex-row item-center">
        <ul className="list-disc pl-8 sm:text-base md:text-lg lg:text-xl max-w-6xl mx-auto space-y-3 text-gray-700">
          {expertise.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <div className="w-full md:w-1/3 lg:w-[40%]">
          <img src="../assets/images/devops-pages/grafana-image.png" alt="" />
        </div>
        </div>
      </section>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Use Cases
        </h2>
      </section>
      <div className="rounded-3xl p-6 w-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
          {useCases.map((useCase) => (
            <div
              key={useCase.title}
              className="relative flex flex-col space-y-4 rounded-3xl shadow-md p-6 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
              
            >
              <h3 className="text-lg font-bold text-gray-900">
                {useCase.title}
              </h3>
              <p className="text-lg text-gray-700">{useCase.description}</p>
            </div>
          ))}
        </div>
      </div>

      <section className="text-center mb-5 mt-10 w-full">
        <h2 className="sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800">
          Why Choose Ayanshtech
        </h2>
      </section>
      <div className="p-6 sm:p-8 md:p-12 lg:p-16 w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reasons.map((reason) => (
            <div
              key={reason}
              className="flex items-start space-x-4 rounded-2xl shadow-md p-4 border border-gray-100
        transition-all duration-300 ease-out transform-gpu
        hover:scale-105 hover:shadow-1xl
        hover:bg-gradient-to-l hover:from-blue-300 hover:to-green-200 hover:border-none"
            >
              <div className="h-3 w-3 rounded-full bg-green-600 mt-2 flex-shrink-0" />
              <p className="text-lg text-gray-700">{reason}</p>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <button
            onClick={scrollToContact}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-full text-lg transition duration-300"
          >
            Elevate Observability
          </button>
        </div>
      </div>

      <div id="contact-section" className="w-full mt-12">
        <ContactUs />
      </div>
    </div>
  </>
);

export default Monitoring;

